import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { CalendarCheck, Clock, PawPrint, User, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function StaffDashboard() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [noteModal, setNoteModal] = useState(null);
  const [notes, setNotes] = useState('');

  const dateStr = currentDate.toISOString().split('T')[0];

  const fetchAppointments = async () => {
    setLoading(true);
    try { const res = await api.get(`/staff/appointments?date=${dateStr}`); setAppointments(res.data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchAppointments(); }, [dateStr]);

  const prevDay = () => setCurrentDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; });
  const nextDay = () => setCurrentDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; });

  const completeAppointment = async (id) => {
    try { await api.put(`/staff/appointments/${id}/complete`); toast.success('Appointment completed!'); fetchAppointments(); }
    catch { toast.error('Failed to complete'); }
  };

  const saveNotes = async () => {
    if (!noteModal) return;
    try { await api.put(`/staff/appointments/${noteModal}/notes`, { notes }); toast.success('Notes saved'); setNoteModal(null); fetchAppointments(); }
    catch { toast.error('Failed to save notes'); }
  };

  const statusColor = (s) => {
    const map = { confirmed: 'bg-blue-100 text-blue-800', in_progress: 'bg-yellow-100 text-yellow-800', completed: 'bg-green-100 text-green-800' };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>My Schedule</h1>
        <p className="text-sm text-[#5A5A5A] mt-1">Welcome, {user?.name}</p>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" className="rounded-lg" onClick={prevDay} data-testid="staff-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
        <h2 className="text-lg font-semibold text-[#2D2D2D]">
          {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </h2>
        <Button variant="outline" size="sm" className="rounded-lg" onClick={nextDay} data-testid="staff-next-day"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>
      ) : appointments.length === 0 ? (
        <Card className="rounded-2xl border-[#E5E5E0] border-dashed bg-white"><CardContent className="p-8 text-center"><CalendarCheck className="w-10 h-10 text-[#D6D6D0] mx-auto mb-3" /><p className="text-sm text-[#5A5A5A]">No appointments for this date</p></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {appointments.map(appt => (
            <Card key={appt.appointment_id} className="rounded-2xl border-[#E5E5E0] bg-white" style={{ boxShadow: 'var(--shadow-soft)' }} data-testid={`staff-appt-${appt.appointment_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#F5F5F0] rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#2D2D2D]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-base font-semibold text-[#2D2D2D]">{appt.time_slot} - {appt.end_time}</p>
                      <p className="text-sm text-[#5A5A5A]">{appt.service_names?.join(', ')}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusColor(appt.status)}`}>{appt.status}</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4 bg-[#F9F9F7] rounded-xl p-4">
                  <div>
                    <p className="text-xs text-[#888] font-semibold uppercase mb-1">Client</p>
                    <p className="text-sm font-medium text-[#2D2D2D]">{appt.client_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#888] font-semibold uppercase mb-1">Pet</p>
                    <p className="text-sm font-medium text-[#2D2D2D]">{appt.pet?.name} ({appt.pet?.breed})</p>
                    <p className="text-xs text-[#5A5A5A]">{appt.pet?.species} | {appt.pet?.gender} | {appt.pet?.weight}kg</p>
                  </div>
                  {appt.pet?.behavioral_notes && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-[#888] font-semibold uppercase mb-1">Behavioral Notes</p>
                      <p className="text-sm text-[#5A5A5A]">{appt.pet.behavioral_notes}</p>
                    </div>
                  )}
                  {appt.pet?.allergies?.length > 0 && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-[#888] font-semibold uppercase mb-1">Allergies</p>
                      <p className="text-sm text-[#D9534F]">{appt.pet.allergies.join(', ')}</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  {appt.status !== 'completed' && (
                    <Button className="rounded-full bg-[#4A7C59] text-white hover:bg-[#3d6849] text-sm" onClick={() => completeAppointment(appt.appointment_id)} data-testid={`complete-appt-${appt.appointment_id}`}>
                      <CheckCircle className="w-4 h-4 mr-1" /> Mark Complete
                    </Button>
                  )}
                  <Button variant="outline" className="rounded-full text-sm" onClick={() => { setNoteModal(appt.appointment_id); setNotes(appt.staff_notes || ''); }} data-testid={`add-notes-${appt.appointment_id}`}>
                    Add Notes
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {noteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Card className="rounded-2xl w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4">Session Notes</h3>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="rounded-lg mb-4" placeholder="Add grooming notes, observations, or issues..." data-testid="staff-notes-textarea" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" className="rounded-full" onClick={() => setNoteModal(null)}>Cancel</Button>
                <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={saveNotes} data-testid="save-notes-btn">Save Notes</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
